// @flow
import { stylesheet } from 'typestyle';

import LegacyComparisonGridDiamonds from './LegacyComparisonGridDiamonds';
import CompareBubble from './CompareBubble';
import { searchPath as diamondSearch } from '@src/app_routes/diamonds';
import { searchPath as settingSearch } from '@src/app_routes/settings';

import LinkPopup from '../popups/Link';
import * as ApproximateSizeShapeContent from '../popups/content/ApproximateSizeShape';

import SettingCatalogItem from '@components/ringSearch/CatalogItem';
import BandCatalogItem from '@components/bandSearch/CatalogItem';

import { linkBlueText } from '@styles/general';

import type { DiamondType } from '@types/diamond';
import type { TFavoriteSetting } from '@types/setting';

type PropsType = {
  diamonds: Array<DiamondType>,
  settings: Array<TFavoriteSetting>,
  onNameClick: (N: string) => void,
  onRemoveDiamondClick: (ID: string) => void,
  sortColumn?: string,
  sortDirection?: string,
  loggedIn: boolean
};

const css = stylesheet({
  settings: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingBottom: 50,

    $nest: {
      '& a': {
        color: `${linkBlueText} !important`,
        textDecoration: 'none !important'
      },

      '& a:hover, & a:active, & a:focus': {
        textDecoration: 'underline !important'
      }
    }
  }
});

function LegacyComparisonGrid(props: PropsType) {
  const {
    diamonds,
    settings,
    onNameClick,
    onRemoveDiamondClick,
    sortColumn,
    sortDirection,
    loggedIn
  } = props;

  if (settings.length === 0 && diamonds.length === 0) {
    return (
      <div>
        <p style={{ clear: 'both', paddingTop: '10px', fontWeight: 'bold' }}>
          {loggedIn ? null : <span>Log-in now to view your saved items.</span>}
          <br />
          <br />
          To begin saving and comparing items, search for a{' '}
          <a href={diamondSearch()}>diamond</a> or{' '}
          <a href={settingSearch()}>setting</a> of your choice.
        </p>
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
      </div>
    );
  } else {
    return (
      <div>
        {settings.length === 0 ? null : (
          <div id='favorite-settings' className={css.settings}>
            {settings.map(s => {
              if (s.is_band) {
                return <BandCatalogItem key={s.vendor_sku} item={s} />;
              } else {
                return <SettingCatalogItem key={s.vendor_sku} item={s} />;
              }
            })}
          </div>
        )}
        {diamonds.length === 0 ? null : (
          <div id='compare'>
            <table className='fixed-table compare-table'>
              <tbody>
                <tr>
                  <td>
                    <table id='compareLeftTable' className='compare-table'>
                      <tbody>
                        <tr>
                          <td
                            className='zero-line-height'
                            style={{ height: '30px' }}
                          >
                            &nbsp;
                          </td>
                        </tr>
                        <tr>
                          <td
                            className='approximate'
                            style={{ height: '85px' }}
                          >
                            <LinkPopup
                              initialContent={ApproximateSizeShapeContent}
                            >
                              Approximate
                            </LinkPopup>
                            <p>
                              Size &amp; Shape
                              <br />
                              (millimeters)
                            </p>
                          </td>
                        </tr>
                        <tr>
                          <td
                            className='zero-line-height'
                            style={{ height: '30px' }}
                          >
                            &nbsp;
                          </td>
                        </tr>
                        <tr>
                          <td
                            className='zero-line-height'
                            style={{ height: '30px' }}
                          >
                            &nbsp;
                          </td>
                        </tr>
                        <tr>
                          <th
                            style={{ height: '30px' }}
                            className='compare-table-header'
                          >
                            <CompareBubble
                              name='Price'
                              bubbleName='retail_price'
                              onNameClick={onNameClick}
                              sortColumn={sortColumn}
                              sortDirection={sortDirection}
                            />
                          </th>
                        </tr>
                        <tr>
                          <th
                            style={{ height: '30px' }}
                            className='compare-table-header'
                          >
                            <CompareBubble
                              name='Carat Weight'
                              bubbleName='carat'
                              onNameClick={onNameClick}
                              sortColumn={sortColumn}
                              sortDirection={sortDirection}
                            />
                          </th>
                        </tr>
                        <tr>
                          <th
                            style={{ height: '30px' }}
                            className='compare-table-header'
                          >
                            <CompareBubble
                              name='Cut'
                              onNameClick={onNameClick}
                              sortColumn={sortColumn}
                              sortDirection={sortDirection}
                            />
                          </th>
                        </tr>
                        <tr>
                          <th
                            style={{ height: '30px' }}
                            className='compare-table-header'
                          >
                            <CompareBubble
                              name='Color'
                              onNameClick={onNameClick}
                              sortColumn={sortColumn}
                              sortDirection={sortDirection}
                            />
                          </th>
                        </tr>
                        <tr>
                          <th
                            style={{ height: '30px' }}
                            className='compare-table-header'
                          >
                            <CompareBubble
                              name='Clarity'
                              onNameClick={onNameClick}
                              sortColumn={sortColumn}
                              sortDirection={sortDirection}
                            />
                          </th>
                        </tr>
                        <tr>
                          <th
                            style={{ height: '30px' }}
                            className='compare-table-header'
                          >
                            <CompareBubble
                              name='Polish'
                              onNameClick={onNameClick}
                              sortColumn={sortColumn}
                              sortDirection={sortDirection}
                            />
                          </th>
                        </tr>
                        <tr>
                          <th
                            style={{ height: '30px' }}
                            className='compare-table-header'
                          >
                            <CompareBubble
                              name='Symmetry'
                              onNameClick={onNameClick}
                              sortColumn={sortColumn}
                              sortDirection={sortDirection}
                            />
                          </th>
                        </tr>
                        <tr>
                          <th
                            style={{ height: '30px' }}
                            className='compare-table-header'
                          >
                            <CompareBubble
                              name='Lab'
                              onNameClick={onNameClick}
                              sortColumn={sortColumn}
                              sortDirection={sortDirection}
                            />
                          </th>
                        </tr>
                        <tr>
                          <th
                            style={{ height: '30px' }}
                            className='compare-table-header'
                          >
                            <CompareBubble
                              name='L/W Ratio'
                              bubbleName='lw_ratio'
                              onNameClick={onNameClick}
                              sortColumn={sortColumn}
                              sortDirection={sortDirection}
                            />
                          </th>
                        </tr>
                        <tr>
                          <th
                            style={{ height: '40px' }}
                            className='large compare-table-header'
                          >
                            <CompareBubble
                              name='Measurements'
                              popupName='DiamondDimensions'
                              onNameClick={onNameClick}
                              sortColumn={sortColumn}
                              sortDirection={sortDirection}
                            />
                          </th>
                        </tr>
                        <tr>
                          <th
                            style={{ height: '30px' }}
                            className='compare-table-header'
                          >
                            <CompareBubble
                              name='Depth %'
                              bubbleName='depth'
                              onNameClick={onNameClick}
                              sortColumn={sortColumn}
                              sortDirection={sortDirection}
                            />
                          </th>
                        </tr>
                        <tr>
                          <th
                            style={{ height: '30px' }}
                            className='compare-table-header'
                          >
                            <CompareBubble
                              name='Table %'
                              bubbleName='table'
                              onNameClick={onNameClick}
                              sortColumn={sortColumn}
                              sortDirection={sortDirection}
                            />
                          </th>
                        </tr>
                        <tr>
                          <th
                            style={{ height: '30px' }}
                            className='compare-table-header'
                          >
                            <CompareBubble
                              name='Fluorescence'
                              bubbleName='fluor'
                              onNameClick={onNameClick}
                              sortColumn={sortColumn}
                              sortDirection={sortDirection}
                            />
                          </th>
                        </tr>
                        <tr>
                          <th
                            style={{ height: '30px' }}
                            className='compare-table-header'
                          >
                            <CompareBubble
                              name='Culet'
                              onNameClick={onNameClick}
                              sortColumn={sortColumn}
                              sortDirection={sortDirection}
                            />
                          </th>
                        </tr>
                        <tr>
                          <th
                            style={{ height: '40px' }}
                            className='large compare-table-header'
                          >
                            <CompareBubble
                              name='Girdle'
                              onNameClick={onNameClick}
                              sortColumn={sortColumn}
                              sortDirection={sortDirection}
                            />
                          </th>
                        </tr>
                        <tr>
                          <th
                            style={{ height: '40px' }}
                            className='large compare-table-header'
                          >
                            <CompareBubble
                              name='Price per carat'
                              bubbleName='retail_per_carat'
                              onNameClick={onNameClick}
                              sortColumn={sortColumn}
                              sortDirection={sortDirection}
                            />
                          </th>
                        </tr>
                        <tr>
                          <th
                            style={{ height: '30px' }}
                            className='compare-table-header'
                          >
                            <CompareBubble
                              name='Item Number'
                              bubbleName='sku'
                              onNameClick={onNameClick}
                              sortColumn={sortColumn}
                              sortDirection={sortDirection}
                            />
                          </th>
                        </tr>
                        <tr>
                          <td
                            className='zero-line-height'
                            style={{ height: '15px' }}
                          >
                            &nbsp;
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                  <td>
                    <LegacyComparisonGridDiamonds
                      diamonds={diamonds}
                      onRemoveClick={onRemoveDiamondClick}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }
}

export default LegacyComparisonGrid;
